import React, { useState, useEffect, useRef, FC } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { BUTTON_TEXT_LOADING, NO_RECORDS, LABEL_HOW_DID_YOU_HEAR } from '../../../shared/lib/constants';
import { SocialSource } from '../generated';
import { useFetchSocialSourcesLazyQuery } from '../../../admin/src/generated';
import { SocialSourceProps } from '../app/interfaceTypes';

const PAGE_LIMIT = 10;

const SocialSourceSelector: FC<SocialSourceProps> = ({ value, onChange }) => {
  const [options, setOptions] = useState<SocialSource[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);

  // lazy query
  const [fetchSources, { loading }] = useFetchSocialSourcesLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const { fetchSocialSources } = data || {};
      const socialSources = (fetchSocialSources && fetchSocialSources.socialSources) || [];
      const pagination = fetchSocialSources?.pagination;

      console.debug('[SocialSource] onCompleted', { page, received: socialSources?.length, pagination });

      setOptions((prev) => {
        if (page === 1) {
          // replace on first page (fresh load)
          return (socialSources as SocialSource[]) || [];
        }
        // append unique
        const next = [...prev];
        (socialSources as SocialSource[]).forEach((s) => {
          if (!next.some((p) => p.id === s.id)) next.push(s);
        });
        return next;
      });

      if (pagination) {
        const { totalPages, page: currentPage } = pagination;
        setHasMore(!(totalPages && currentPage && currentPage >= totalPages));
      } else {
        // fallback: if we received fewer than PAGE_LIMIT, assume no more
        setHasMore((socialSources as any[]).length === PAGE_LIMIT);
      }
    },
    onError: (err) => {
      console.error('[SocialSource] fetch error', err);
    }
  });

  // When the dropdown opens, reset to page 1 and fetch.
  useEffect(() => {
    if (open) {
      setPage(1); // trigger fetch via the next effect
    }
  }, [open]);

  // Fetch whenever `page` changes AND dropdown is open.
  useEffect(() => {
    if (!open) return;
    console.debug('[SocialSource] fetching page', page);
    fetchSources({ variables: { input: { page, limit: PAGE_LIMIT } } });
  }, [page, open, fetchSources]);

  // listbox ref (optional, for debugging)
  const listboxRef = useRef<HTMLUListElement | null>(null);

  // scroll handler (passed directly via ListboxProps.onScroll)
  const handleListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.currentTarget as HTMLElement;
    // debug
    // console.debug("listbox scroll", { scrollTop: target.scrollTop, clientHeight: target.clientHeight, scrollHeight: target.scrollHeight });
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20 && hasMore && !loading) {
      setPage((p) => p + 1);
    }
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      options={options}
      getOptionLabel={(option) => (option?.name as string) || ''}
      loading={loading}
      // If you want server-side search (typing triggers new API results), remove or change this:
      filterOptions={(x) => x} // disables client-side filtering so you can implement server-side filtering if needed
      noOptionsText={loading ? BUTTON_TEXT_LOADING : NO_RECORDS}
      ListboxProps={{
        onScroll: handleListboxScroll,
        ref: (node: HTMLUListElement | null) => {
          listboxRef.current = node;
        },
        style: { maxHeight: 250, overflow: 'auto' }
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1
          }}
        >
          <Typography variant="body2">{option.name}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={LABEL_HOW_DID_YOU_HEAR}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default SocialSourceSelector;
